// Helpers for the Medsender Printer Driver arrival prompt on the record viewer.
//
// A printed document is inserted straight into the records table by polymath
// (the Go print backend). It sets no patient information and stamps the
// organization's default privacy level. That makes it unlike a web upload,
// where the Upload Record modal collects patient details and privacy *before*
// the record exists — a printed record reaches the user already created, so the
// record viewer is the first and only place we can ask.
//
// Gibraltar (the Electron printer client) opens the user's browser at
// /app/view/<file_token>?send=<preference> after every print, and that URL
// shape is the only signal we get. Nothing inside the app navigates to the
// viewer with a `send` param and no `from` param:
//
//   Gibraltar         ->  ?send=true  |  ?send=false        (never sets `from`)
//   in-app navigation ->  ?send=false&from=received&...     (always sets `from`)
//
// See gibraltar/src/renderer/notifications.js, the 'upload_finished' listener.
// Gibraltar cannot be changed without a signed desktop release, so this reads
// the URL it already produces rather than asking it for a clearer marker.

export const isPrinterDriverArrival = (queryParams = {}) => (
  queryParams.send !== undefined && queryParams.from === undefined
);

// The Medsender iOS app opens the same arrival URL with `source=ios` added
// (/app/view/<file_token>?send=false&source=ios): its uploads also arrive with
// no patient details, so they get the same prompt, titled for the app. Read it
// at mount alongside the arrival itself (the URL is rewritten afterwards).
export const MEDSENDER_APP_UPLOAD_SOURCE = 'ios';

export const printerUploadSource = (queryParams = {}) => (
  queryParams.source === MEDSENDER_APP_UPLOAD_SOURCE ? MEDSENDER_APP_UPLOAD_SOURCE : null
);

export const printerUploadPromptTitle = (modalData = {}) => (
  modalData?.uploadSource === MEDSENDER_APP_UPLOAD_SOURCE
    ? 'Record uploaded from the Medsender app'
    : 'Record uploaded from Printer Driver'
);

// RecordViewer#componentDidMount rewrites the arrival URL to `?send=false` (see
// removeSendDialogParam in actions/appActions.js), which is indistinguishable
// from a fresh Gibraltar arrival — so on a refresh the URL alone would re-prompt
// for a record the user already answered for. The "already asked" marker
// therefore lives in sessionStorage instead of the query string: scoped to the
// tab, keyed by file token, and gone when the tab closes. A genuinely new print
// carries a new token and prompts normally.
const promptKey = (fileToken) => `printer_upload_prompted:${fileToken}`;

export const printerUploadAlreadyPrompted = (fileToken) => {
  if (!fileToken) return false;
  try {
    return window.sessionStorage.getItem(promptKey(fileToken)) === 'true';
  } catch {
    // Locked-down browsers throw on sessionStorage access. Losing the marker
    // costs one extra prompt after a refresh; throwing would break the viewer.
    return false;
  }
};

export const markPrinterUploadPrompted = (fileToken) => {
  if (!fileToken) return;
  try {
    window.sessionStorage.setItem(promptKey(fileToken), 'true');
  } catch {
    // See above — a failed write only costs a repeat prompt.
  }
};

// True when EditPatient was opened by the arrival prompt rather than by the
// "Edit Record Info" toolbar button.
export const printerUploadPromptEnabled = (modalData = {}) => (
  modalData?.promptForPrinterUpload === true
);

// Deliberately the same localStorage key the web Upload Record modal uses
// (UploadSingleRecord/index.jsx): "use this level for all future uploads" is a
// statement about the user's uploads, not about which button they pressed, so
// setting it in either place is honoured in both. Unlike the upload modal, the
// fallback here is the record's real current level rather than a hardcoded
// 'department' — the record already exists and already has one.
const REMEMBERED_PRIVACY_KEY = 'privacy_level';

export const rememberedPrivacyLevel = (fallback) => {
  try {
    return window.localStorage.getItem(REMEMBERED_PRIVACY_KEY) || fallback;
  } catch {
    return fallback;
  }
};

export const rememberPrivacyLevel = (privacyLevel, shouldRemember) => {
  try {
    if (shouldRemember) {
      // No level to remember is not the same as "stop remembering": the privacy
      // control is hidden whenever the user may not edit the level, and wiping
      // a preference they set elsewhere on the strength of a blank field would
      // silently undo it. Only an unchecked box clears the stored value.
      if (!privacyLevel) return;

      window.localStorage.setItem(REMEMBERED_PRIVACY_KEY, privacyLevel);
    } else {
      window.localStorage.removeItem(REMEMBERED_PRIVACY_KEY);
    }
  } catch {
    // Best-effort preference; a failed write just means we ask again next time.
  }
};

// Unchecking "use this level for all future uploads" is an answer in its own
// right, and it should not depend on the user going on to save: this drops the
// stored preference the moment the box is cleared. Checking the box again
// stores nothing until a save succeeds, which is the level the record actually
// ended up on.
export const forgetPrivacyLevel = () => rememberPrivacyLevel(null, false);
